import { Enum } from '@lighthouse/tools'

import type {
    AlignMent,
    AnchorProtocol,
    Arrangement,
    BackgroundNoImageProtocol,
    BlockSize,
    ButtonItem,
    CreateProtocol,
    CursorProtocol,
    CustomStatisticConfig,
    DataPointerProtocol,
    DeleteProtocol,
    Direction,
    DisplayProtocol,
    EdgeValue,
    EditableProtocol,
    ExportProtocol,
    GroupByProtocol,
    GroupConfigure,
    HighlightingProtocol,
    ImportProtocol,
    Order,
    PaginableProtocol,
    PaginationProtocol,
    Position,
    PrintProtocol,
    RectSizeUnit,
    RequiredProtocol,
    SearchableProtocol,
    SearchProtocol,
    SortableProtocol,
    SortProtocol,
    TableRowHeightProtocol,
    ViewBreakPointProtocol,
    ViewFieldSettingProtocol,
    ViewFilterableProtocol
} from '../../protocols'
import type { EventsProtocol } from '../../protocols/event'
import type { TitleConfig } from '../Basic'
import { BlockType } from '../Basic'
import { BaseBlock } from './BaseBlock'
import type { BlockAbstract } from './Block'

/** ********************************* Table options ********************************* */
export const VIEW_DESIGN_STYLE = Enum('classic', 'simple', 'stripe')
export type ViewDesignStyle = Enum<typeof VIEW_DESIGN_STYLE>

export const VIEW_DESIGN_TYPE = Enum('default', 'card', 'colorBlock')
export type ViewDesignType = 'default' | 'card' | 'colorBlock'

export interface TableViewOptions {
    designStyle?: ViewDesignStyle
    designType?: ViewDesignType
    listAvatar?: string
    isShowListAvatar?: boolean
    avatarType?: 'round' | 'square'
    isShowEmphasizeText?: boolean
    emphasizeTextField?: string
    hiddenHeader?: boolean
}

/** ********************************* kanban options ********************************* */
/**
 * 看板视图配置
 * @date 9/7/2023 - 2:14:29 PM
 *
 * @export
 * @interface KanbanViewOptions
 * @typedef {KanbanViewOptions}
 */
export interface KanbanViewOptions {
    /**
     * 看板分组字段
     * @date 10/26/2023 - 2:13:37 PM
     *
     * @type {?GroupConfigure}
     */
    kanbanGroupConfigure?: GroupConfigure

    /**
     * 允许设置看板
     * @date 9/7/2023 - 2:13:54 PM
     *
     * @type {?boolean}
     */
    canSetKanban?: boolean

    /**
     * 允许拖拽看板
     * @date 9/7/2023 - 2:14:08 PM
     *
     * @type {?boolean}
     */
    canSortKanban?: boolean
}

/** ********************************* calendar options ********************************* */
/**
 * 日历视图配置
 * @date 9/7/2023 - 2:11:17 PM
 *
 * @export
 * @interface CalendarViewOptions
 * @typedef {CalendarViewOptions}
 */
export interface CalendarViewOptions {
    /**
     * 日程标题字段
     * @date 9/7/2023 - 1:58:14 PM
     *
     * @type {?string}
     */
    scheduleTitleField?: string

    /**
     * 日程开始日期字段
     * @date 9/7/2023 - 1:58:56 PM
     *
     * @type {?string}
     */
    scheduleStartDateField?: string

    /**
     * 日程结束日期字段
     * @date 9/7/2023 - 1:59:52 PM
     *
     * @type {?string}
     */
    scheduleEndDateField?: string

    /**
     * 日程颜色展示方式 follow: 跟随字段选项值、或主题色, custom: 自定义颜色
     * @default follow
     * @date 9/7/2023 - 2:08:03 PM
     *
     * @type {?('follow' | 'custom')}
     */
    scheduleColorMode?: 'follow' | 'custom'

    /**
     * 日程颜色
     * @date 9/7/2023 - 2:04:04 PM
     *
     * @type {?string}
     */
    scheduleColor?: string

    /**
     * 日程颜色根据选项字段的颜色来
     * @date 9/7/2023 - 2:04:04 PM
     *
     * @type {?string}
     */
    scheduleColorField?: string
}

/** ********************************* custom view options ********************************* */
export const CUSTOM_VIEW_LAYOUT = Enum(
    'waterfall',
    'grid',
    'carousel',
    'weekCalendar',
    'monthCalendar',
    'comment',
    'tree',
    'multiDimensional'
)
export type CustomViewLayout = Enum<typeof CUSTOM_VIEW_LAYOUT>

export interface CarouselViewOptions {
    autoplay?: boolean
    interval?: number
    loop?: boolean
    navigation?: boolean
    pagination?: boolean
    swipe?: boolean
    effect?: 'slide' | 'creative'
}

export const DEFAULT_CAROUSEL_VIEW_OPTIONS: Required<CarouselViewOptions> = {
    autoplay: true,
    interval: 3,
    loop: true,
    navigation: true,
    pagination: true,
    swipe: true,
    effect: 'slide'
}

export interface ViewLayout {
    layout?: CustomViewLayout
    direction?: Direction
    wrap?: boolean
    arrangement?: Arrangement
    size?: Exclude<BlockSize, 'xs'>
    cols?: number // 每行显示
    gap?: { size?: number; unit?: Exclude<RectSizeUnit, 'vw' | 'vh'> } // 间距
    viewPadding?: { size?: EdgeValue; unit?: Exclude<RectSizeUnit, 'vw' | 'vh'> } // 边距
    carousel?: CarouselViewOptions

    // 评论组件-子级缩进
    childIndent?: number

    // 月/周日历：单元格宽度自适应（开启后列宽均分容器，无最小值约束）
    adaptive?: boolean
}

export interface ViewStyles {
    // 周日历
    /** 日期行 */
    calendarDateRow?: {
        weekColor?: BackgroundNoImageProtocol
        dateColor?: BackgroundNoImageProtocol
        background?: BackgroundNoImageProtocol
    }
    /** 时间列 */
    calendarTimeCol?: {
        timeColor?: BackgroundNoImageProtocol
        background?: BackgroundNoImageProtocol
    }

    /** 显示网格 */
    showGrid?: boolean

    /** 网格颜色 */
    gridColor?: BackgroundNoImageProtocol

    /** 隐藏时间段拆分（仅周日历），true = 不按时间归类且不渲染时间列 */
    hideTimeSplit?: boolean

    /** 显示周切换（仅周日历），true = 日历外部顶上渲染上一周/下一周 */
    showWeekSwitcher?: boolean

    /** 月/周日历：有记录时空格子跟随同行最高高度；当前面板无记录时格子按 1:1 占位 */
    uniformRowHeight?: boolean

    // 评论组件
    /** 回复控件的样式 */
    replyStyle?: {
        color?: BackgroundNoImageProtocol
        iconColor?: BackgroundNoImageProtocol
    }
}

/** ********************************* gallery options ********************************* */
export const GALLERY_AVATAR_SIZE = Enum('sm', 'md', 'lg', 'xl')
export type GalleryAvatarSize = Enum<typeof GALLERY_AVATAR_SIZE>

export const GALLERY_AVATAR_SHAPE = Enum('square', 'circle')
export type GalleryAvatarShape = Enum<typeof GALLERY_AVATAR_SHAPE>

export const GALLERY_CARD_STYLE = Enum('block', 'border', 'none')
export type GalleryCardStyle = Enum<typeof GALLERY_CARD_STYLE>

/** 伸缩方式 */
export interface ObjectFitConfig {
    objectFit?: Extract<React.CSSProperties['objectFit'], 'contain' | 'cover'>
}
export interface RatioConfig {
    ratio?: string
}
/** 摆放位置 */
export interface PositionConfig {
    position?: Exclude<Position, 'bottom'>
}

/** 字段基本设置 */
export interface BaseFieldConfig {
    /** 是否显示 */
    show?: boolean
    /** 展示字段 */
    field?: string
    /** 对齐方式 */
    align?: AlignMent
}

/** 字段显示方式 */
export type GroupFieldValue = {
    // showTitle: boolean
    wrap: boolean
    // title: string
    id: string
}

/**
 * 画廊视图配置
 * @date 9/7/2023 - 2:15:12 PM
 *
 * @export
 * @interface GalleryViewOptions
 * @typedef {GalleryViewOptions}
 */
export interface GalleryViewOptions {
    /** 封面 */
    headCover?: Omit<BaseFieldConfig, 'align'> & ObjectFitConfig & RatioConfig
    /** 头像 */
    headAvatar?: BaseFieldConfig &
        PositionConfig & {
            size?: GalleryAvatarSize
            shape?: GalleryAvatarShape
        }
    /** 标题 */
    headTitle?: BaseFieldConfig
    /** 标签 */
    headTags?: {
        show?: boolean
        align?: AlignMent
        fields?: GroupFieldValue[]
        /** 显示字段名 */
        showLabel?: boolean
    }
    /** 内容 */
    contentTags?: {
        show?: boolean
        align?: AlignMent
        fields?: GroupFieldValue[]
        /** 显示字段名 */
        showLabel?: boolean
    }
    /** 尾部 */
    footTags?: {
        show?: boolean
        align?: AlignMent
        fields?: GroupFieldValue[]
        /** 显示字段名 */
        showLabel?: boolean
    }

    /**
     * 卡片风格
     * @date 6/28/2023 - 5:17:05 PM
     *
     * @type {?GalleryCardStyle}
     */
    style?: GalleryCardStyle
}

export const FIRST_DAY_OF_WEEK = Enum('TODAY', 'MONDAY', 'SUNDAY')
export type FirstDayOfWeek = Enum<typeof FIRST_DAY_OF_WEEK>

namespace CustomView {
    export interface CalendarViewOptions {
        /** 日历开始日期字段（月日历/周日历共用） */
        calendarStartDateField?: string
        /** 日历结束日期字段（仅月日历使用，必填） */
        calendarEndDateField?: string
        /** 周起始于（仅周日历使用） */
        firstDayOfWeek?: FirstDayOfWeek
    }

    export interface CommentViewOptions {
        /** 回复字段 */
        commentConfig?: {
            rootId: string
            parentId: string
        }
    }

    export interface TreeViewOptions {
        /** 树形层级结构 */
        treeConfig?: {
            parentId: string
            sortId: string
        }
        /** 可拖拽排序 */
        canDragTreeSort?: boolean
    }

    export interface MultiDimensionalViewOptions {
        /** 多维视图配置 */
        multiDimensionalConfig?: {
            /** 行维度 */
            rowDimension?: string
            /** 行维度排序字段 */
            rowDimensionSortField?: string
            /** 行维度排序方式 */
            rowDimensionSortMode?: Order
            /** 列维度 */
            columnDimension?: string
            /** 列维度排序字段 */
            columnDimensionSortField?: string

            /** 列维度排序方式 */
            columnDimensionSortMode?: Order
        }
    }

    export interface GridSortOptions {
        /** 可拖拽排序（网格/列表视图） */
        canDragViewSort?: boolean
        /** 网格视图排序配置 */
        viewSortConfig?: {
            sortId: string
        }
    }
}

/**
 * 自定义视图配置
 *
 * @export
 * @interface CustomViewOptions
 * @typedef {CustomViewOptions}
 */
export interface CustomViewOptions
    extends CustomView.CalendarViewOptions,
        CustomView.CommentViewOptions,
        CustomView.MultiDimensionalViewOptions,
        CustomView.TreeViewOptions,
        CustomView.GridSortOptions {
    customStatistic?: CustomStatisticConfig
}

export interface ToolbarProtocol {
    toolbar?: ButtonItem[]
}

export interface RecordOperatorProtocol {
    recordOperator?: ButtonItem[]
}
/** 视图通用配置项 */
interface CommonViewOptions
    extends TitleConfig,
        DataPointerProtocol,
        GroupByProtocol,
        ViewFilterableProtocol,
        SortableProtocol,
        PaginationProtocol,
        EditableProtocol,
        RequiredProtocol,
        SearchableProtocol,
        CreateProtocol,
        DeleteProtocol,
        ImportProtocol,
        ExportProtocol,
        PrintProtocol,
        HighlightingProtocol,
        SortProtocol,
        DisplayProtocol,
        PaginableProtocol,
        ViewFieldSettingProtocol,
        SearchProtocol,
        ToolbarProtocol,
        RecordOperatorProtocol,
        EventsProtocol,
        AnchorProtocol {
    /** 表格行高 */
    tableRowHeight?: TableRowHeightProtocol
    cursor?: CursorProtocol
}

export type TableViewConfig = Record<'viewType', 'table'> & CommonViewOptions & TableViewOptions & ViewBreakPointProtocol
export type ListViewConfig = Record<'viewType', 'list'> & CommonViewOptions & TableViewOptions & ViewBreakPointProtocol
export type AdvancedTableViewViewConfig = Record<'viewType', 'advancedTable'> & CommonViewOptions & ViewBreakPointProtocol
export type GalleryViewConfig = Record<'viewType', 'gallery'> & CommonViewOptions & GalleryViewOptions & ViewBreakPointProtocol
export type KanbanViewConfig = Record<'viewType', 'kanban'> & CommonViewOptions & KanbanViewOptions & ViewBreakPointProtocol
export type CalendarViewConfig = Record<'viewType', 'calendar'> & CommonViewOptions & CalendarViewOptions & ViewBreakPointProtocol
export type CustomViewConfig = Record<'viewType', 'custom'> & CommonViewOptions & CustomViewOptions & ViewBreakPointProtocol

export type ViewOptions =
    | TableViewConfig
    | ListViewConfig
    | AdvancedTableViewViewConfig
    | GalleryViewConfig
    | KanbanViewConfig
    | CalendarViewConfig
    | CustomViewConfig

export abstract class ViewBlockAbstract extends BaseBlock {
    type = BlockType.view

    abstract override config: ViewOptions

    abstract children: BlockAbstract[]
}
